import type { FC } from "react";

import styled from "styled-components";

import { EmptyState } from "@similarweb/ui-components";

import type { SubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import LOBLegends from "../../../components/LOBLegends";

import CategoryCheckboxDropdown from "./CategoryCheckboxDropdown/CategoryCheckboxDropdown";
import TrafficShareBarList from "./TrafficShareBarList";
import useVisitsBreakdownCompareMode from "./useVisitsBreakdownCompareMode";

const ChartContainer = styled(FlexRow)`
    padding: 24px;
    gap: 24px;
    width: 100%;
`;

const StyledEmptyState = styled(EmptyState)`
    width: 100%;
`;

interface IVisitsBreakdownCompareModeProps {
    subtitleFilters: SubtitleFilters;
}

const VisitsBreakdownCompareMode: FC<IVisitsBreakdownCompareModeProps> = ({ subtitleFilters }) => {
    const {
        allChartData,
        legendItems,
        setLegendItems,
        visibleChartData,
        selectedLobs,
        handleSelectedLobsChange,
        handleDropdownClose,
        isLoading,
        availableLobs,
        lobVisitsMap,
    } = useVisitsBreakdownCompareMode();

    const columnTitles: [string, string] = [
        translate("analysis.content.lob.legend.title.value.domain"),
        translate("analysis.content.lob.legend.title.value.visits"),
    ];

    const renderChart = () => {
        const hasSelectedLobs = visibleChartData.some((item) => !item.isTotal);

        if (hasSelectedLobs) {
            return (
                <TrafficShareBarList
                    legendItems={legendItems}
                    data={visibleChartData}
                />
            );
        } else {
            return (
                <StyledEmptyState
                    title=""
                    subtitle={translate("analysis.content.lob.no.data")}
                />
            );
        }
    };

    const renderCategories = () => {
        return (
            <CategoryCheckboxDropdown
                availableLobs={availableLobs}
                selectedLobs={selectedLobs}
                handleSelectedLobsChange={handleSelectedLobsChange}
                handleDropdownClose={handleDropdownClose}
                lobVisitsMap={lobVisitsMap}
            />
        );
    };

    return (
        <WidgetContainer
            data-automation="visits-breakdown-widget"
            title={translate("analysis.content.visits.breakdown.title")}
            tooltip={translate("analysis.content.visits.breakdown.tooltip")}
            subtitleFilters={subtitleFilters}
            withBorder
            isLoading={isLoading}
            noData={!allChartData.length}
            showSubtitleFiltersLoadingState={false}
            loaderHeight={360}
            loaderWidth="100%"
        >
            <FlexRow>
                <ChartContainer>
                    {renderChart()}
                    <div style={{ display: "flex", gap: "12px", flexDirection: "column" }}>
                        {renderCategories()}
                        {legendItems.length > 0 ? (
                            <LOBLegends
                                legendItems={legendItems}
                                setLegendItems={setLegendItems}
                                height="250px"
                                eventName="total visits"
                                hasSearch={false}
                                columnTitles={columnTitles}
                            />
                        ) : null}
                    </div>
                </ChartContainer>
            </FlexRow>
        </WidgetContainer>
    );
};

export default VisitsBreakdownCompareMode;
